import moment from 'moment';

/**
 * Leads page filters (route /crm/leads), kept across refreshes with
 * usePersistedFilters (see docs/FILTER_PERSISTENCE.md).
 *
 * Everything stored is JSON-serializable:
 *   - month:      'MM-YYYY' loaded in funnel / calendar mode
 *   - search:     text typed in the search box (as typed)
 *   - userId:     responsible user id, or null for everyone
 *   - filterBy:   table mode date field ('created_at' | 'proposal_sent_at')
 *   - periodFrom / periodTo: table mode range ('YYYY-MM-DD'), null = default
 *     range (start of the year to today, computed when the request is built)
 *
 * The display mode is a view choice, not a filter: it is kept under its own
 * key so it neither counts in the badge nor is reset by "Limpar tudo".
 */

export var LEADS_FILTERS_KEY = 'crm.leads';
export var LEADS_VIEW_KEY = 'crm.leads.view';

export var LEADS_MONTH_FORMAT = 'MM-YYYY';
var DAY_FORMAT = 'YYYY-MM-DD';

export var LEADS_DISPLAY_MODES = ['table', 'funnel', 'calendar'];
export var DEFAULT_LEADS_DISPLAY_MODE = 'funnel';

export var LEADS_DISPLAY_MODE_MAP = {
  table: { label: 'Tabela', color: '#1890ff', icon: 'table' },
  funnel: { label: 'Funil', color: '#722ed1', icon: 'project' },
  calendar: { label: 'Calendário', color: '#13c2c2', icon: 'calendar' },
};

export var LEADS_FILTER_BY_OPTIONS = [
  { value: 'created_at', label: 'Cadastro' },
  { value: 'proposal_sent_at', label: 'Proposta enviada' },
];

var DEFAULT_COLOR = '#1890ff';
var CUSTOM_COLOR = '#722ed1';

export var getCurrentLeadsMonth = function(now) {
  return moment(now).format(LEADS_MONTH_FORMAT);
};

export var getDefaultLeadsFilters = function(now) {
  return {
    month: getCurrentLeadsMonth(now),
    search: '',
    userId: null,
    filterBy: LEADS_FILTER_BY_OPTIONS[0].value,
    periodFrom: null,
    periodTo: null,
  };
};

export var getDefaultLeadsView = function() {
  return { displayMode: DEFAULT_LEADS_DISPLAY_MODE };
};

export var normalizeLeadsDisplayMode = function(mode) {
  return LEADS_DISPLAY_MODES.indexOf(mode) !== -1 ? mode : DEFAULT_LEADS_DISPLAY_MODE;
};

var isValidMonth = function(month) {
  return typeof month === 'string' && moment(month, LEADS_MONTH_FORMAT, true).isValid();
};

// A restored value that is not a valid 'MM-YYYY' falls back to `fallback`.
export var normalizeLeadsMonth = function(month, fallback) {
  return isValidMonth(month) ? month : fallback;
};

// 'MM-YYYY' -> the /leads query params.
export var getLeadsMonthRequest = function(month) {
  var date = isValidMonth(month) ? moment(month, LEADS_MONTH_FORMAT) : moment();
  return { month: date.month() + 1, year: date.year() };
};

// The IndexedDB cache of the page holds a single month (no month in its key),
// so it is only read and written for the current month. Any other month waits
// for the API instead of flashing the cached month's columns.
export var shouldUseLeadsCache = function(month, currentMonth) {
  return month === currentMonth;
};

export var getDefaultLeadsPeriod = function(now) {
  var today = moment(now);
  return {
    from: today.clone().startOf('year').format(DAY_FORMAT),
    to: today.format(DAY_FORMAT),
  };
};

// Applied range from the period form. Swaps an inverted range and stores the
// default range as null, so "today" keeps moving with the calendar.
export var normalizeLeadsPeriod = function(from, to, now) {
  var start = from;
  var end = to;
  if (start && end && end < start) {
    var swap = start;
    start = end;
    end = swap;
  }
  var defaults = getDefaultLeadsPeriod(now);
  if (start === defaults.from && end === defaults.to) {
    return { periodFrom: null, periodTo: null };
  }
  return { periodFrom: start || null, periodTo: end || null };
};

// Filters -> the /leads/period request used by the table view.
export var getLeadsTablePeriodRequest = function(filters, now) {
  var defaults = getDefaultLeadsPeriod(now);
  return {
    from: filters.periodFrom || defaults.from,
    to: filters.periodTo || defaults.to,
    filterBy: filters.filterBy || LEADS_FILTER_BY_OPTIONS[0].value,
  };
};

export var isLeadsPeriodDefault = function(filters, defaults) {
  return !filters.periodFrom && !filters.periodTo && filters.filterBy === defaults.filterBy;
};

var hasSearch = function(filters) {
  return !!(filters.search && filters.search.trim() !== '');
};

var hasUser = function(filters) {
  return filters.userId !== null && filters.userId !== undefined;
};

/**
 * Keys of the filters that differ from the defaults. With `displayMode`, only
 * the ones that apply to that view: the month drives funnel / calendar, the
 * period drives the table. Search and user filter the loaded columns.
 */
export var getActiveLeadsFilterKeys = function(filters, defaults, displayMode) {
  var keys = [];
  var anyMode = !displayMode;
  if ((anyMode || displayMode !== 'table') && filters.month !== defaults.month) keys.push('month');
  if ((anyMode || displayMode === 'table') && !isLeadsPeriodDefault(filters, defaults)) keys.push('period');
  if (hasUser(filters)) keys.push('user');
  if (hasSearch(filters)) keys.push('search');
  return keys;
};

export var countActiveLeadsFilters = function(filters, defaults, displayMode) {
  return getActiveLeadsFilterKeys(filters, defaults, displayMode).length;
};

var formatDay = function(value) {
  return moment(value, DAY_FORMAT).format('DD/MM/YYYY');
};

export var formatLeadsMonthLabel = function(month) {
  return moment(month, LEADS_MONTH_FORMAT).format('MMM/YYYY');
};

export var formatLeadsPeriodLabel = function(filters, now) {
  var request = getLeadsTablePeriodRequest(filters, now);
  var option = LEADS_FILTER_BY_OPTIONS.find(function(o) { return o.value === request.filterBy; });
  return (option ? option.label : 'Período') + ': ' + formatDay(request.from) + ' - ' + formatDay(request.to);
};

var getUserLabel = function(userId, users) {
  var user = (users || []).find(function(u) { return u && u.id === userId; });
  return user && user.name ? 'Responsável: ' + user.name : 'Usuário filtrado';
};

var buildPill = function(key, filters, defaults, options) {
  var isActive = getActiveLeadsFilterKeys(filters, defaults).indexOf(key) !== -1;
  if (key === 'month') {
    return {
      key: 'month',
      label: formatLeadsMonthLabel(filters.month),
      color: isActive ? CUSTOM_COLOR : DEFAULT_COLOR,
      icon: 'calendar',
      removable: isActive,
    };
  }
  if (key === 'period') {
    return {
      key: 'period',
      label: formatLeadsPeriodLabel(filters, options.now),
      color: isActive ? CUSTOM_COLOR : DEFAULT_COLOR,
      icon: 'calendar',
      removable: isActive,
    };
  }
  if (key === 'user') {
    return { key: 'user', label: getUserLabel(filters.userId, options.users), color: CUSTOM_COLOR, icon: 'user', removable: true };
  }
  return { key: 'search', label: 'Busca: ' + filters.search, color: CUSTOM_COLOR, icon: 'search', removable: true };
};

/**
 * Pills for FilterPillsBar, from the applied filters. The view pill is
 * informative; month / period pills always show for their view and are
 * removable only when they differ from the default.
 * options: { users, now }
 */
export var getLeadsFilterPills = function(filters, defaults, displayMode, options) {
  var opts = options || {};
  var mode = normalizeLeadsDisplayMode(displayMode);
  var modeInfo = LEADS_DISPLAY_MODE_MAP[mode];
  var pills = [{ key: 'mode', label: modeInfo.label, color: modeInfo.color, icon: modeInfo.icon, removable: false }];

  pills.push(buildPill(mode === 'table' ? 'period' : 'month', filters, defaults, opts));
  if (hasUser(filters)) pills.push(buildPill('user', filters, defaults, opts));
  if (hasSearch(filters)) pills.push(buildPill('search', filters, defaults, opts));
  return pills;
};

// Text for RestoredFiltersNotice: every kept filter, whatever the view.
export var getLeadsRestoredSummary = function(filters, defaults, options) {
  var opts = options || {};
  return getActiveLeadsFilterKeys(filters, defaults)
    .map(function(key) { return buildPill(key, filters, defaults, opts).label; })
    .join(' · ');
};

// Partial update that puts one filter back to its default.
export var getLeadsFilterReset = function(key, defaults) {
  if (key === 'month') return { month: defaults.month };
  if (key === 'period') return { filterBy: defaults.filterBy, periodFrom: null, periodTo: null };
  if (key === 'user') return { userId: null };
  if (key === 'search') return { search: '' };
  return {};
};
